import React, { useState, useEffect, useCallback } from "react";
import { getMatrixForAction, upsertAccessRule } from "../../../api/t2tVendorFieldAccessApi";
import { useToast } from "../Layout/T2TUIProvider";

const WORKFLOW_ACTIONS = [
  { key: "create",          label: "Create (4-level)"       },
  { key: "change",          label: "Change (4-level)"       },
  { key: "extend",          label: "Extend (3-level)"       },
  { key: "create_seasonal", label: "Seasonal (2-level)"     },
];

const ROLES = [
  { key: "initiator",    label: "Initiator"             },
  { key: "checker",      label: "Checker"               },
  { key: "finance",      label: "Finance Data Controller"},
  { key: "mdm_approver", label: "MDM Approver"          },
];

const GROUPS = ["initial","general","tax","finance","purchasing","partner"];
const GROUP_LABELS = {
  initial:    "Initial",
  general:    "General Data",
  tax:        "Tax",
  finance:    "Finance",
  purchasing: "Purchasing",
  partner:    "Partner",
};

const ACCESS_LEVELS = ["editable","view_only","no_access"];
const LEVEL_STYLES = {
  editable:  { background: "#DCFCE7", color: "#16A34A", border: "1px solid #BBF7D0" },
  view_only: { background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE" },
  no_access: { background: "#F8FAFC", color: "#94A3B8", border: "1px solid #E2E8F0" },
};

// Build map: "role|group" → access_level
function buildMap(rows) {
  const m = {};
  for (const r of rows) m[`${r.role_code}|${r.group_code}`] = r.access_level;
  return m;
}

const FieldAccessAdmin = () => {
  const toast = useToast();
  const [activeAction, setActiveAction] = useState("create");
  const [matrixMap, setMatrixMap]       = useState({});
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(null); // "role|group" key being saved

  const load = useCallback(async () => {
    setLoading(true);
    const resp = await getMatrixForAction(activeAction);
    if (!resp.error) setMatrixMap(buildMap(resp.data?.data || []));
    setLoading(false);
  }, [activeAction]);

  useEffect(() => { load(); }, [load]);

  const handleChange = async (roleCode, groupCode, newLevel) => {
    const cellKey = `${roleCode}|${groupCode}`;
    const prevLevel = matrixMap[cellKey];
    setMatrixMap((prev) => ({ ...prev, [cellKey]: newLevel }));
    setSaving(cellKey);
    const resp = await upsertAccessRule({
      workflow_action: activeAction,
      role_code:       roleCode,
      group_code:      groupCode,
      access_level:    newLevel,
    });
    setSaving(null);
    if (resp.error) {
      toast(resp.error.message || "Save failed.", "error");
      setMatrixMap((prev) => ({ ...prev, [cellKey]: prevLevel }));
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Role × Workflow Access Matrix</div>
        <div style={{ fontSize: 12, color: "var(--t2t-text-secondary)" }}>
          Controls which screen groups each role can edit, view, or is hidden from — per workflow action.
          Changes save immediately.
        </div>
      </div>

      {/* Workflow action tabs */}
      <div className="t2t-tab-group" style={{ marginBottom: 16 }}>
        {WORKFLOW_ACTIONS.map((a) => (
          <button
            key={a.key}
            className={`t2t-tab${activeAction === a.key ? " active" : ""}`}
            onClick={() => setActiveAction(a.key)}
          >
            {a.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 32, textAlign: "center", fontSize: 12, color: "var(--t2t-text-muted)" }}>Loading…</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#F8FAFC" }}>
                <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, minWidth: 160, borderBottom: "2px solid #E2E8F0" }}>
                  Role
                </th>
                {GROUPS.map((g) => (
                  <th key={g} style={{ padding: "10px 12px", textAlign: "center", fontWeight: 700, borderBottom: "2px solid #E2E8F0", minWidth: 110 }}>
                    {GROUP_LABELS[g]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROLES.map((role) => {
                // Hide roles not in this workflow action
                const hasAnyRule = GROUPS.some((g) => matrixMap[`${role.key}|${g}`]);
                if (!hasAnyRule) return null;
                return (
                  <tr key={role.key} style={{ borderBottom: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "10px 14px", fontWeight: 600 }}>{role.label}</td>
                    {GROUPS.map((g) => {
                      const cellKey = `${role.key}|${g}`;
                      const level   = matrixMap[cellKey] || "view_only";
                      const isSaving = saving === cellKey;
                      return (
                        <td key={g} style={{ padding: "8px 10px", textAlign: "center" }}>
                          <select
                            value={level}
                            disabled={isSaving}
                            onChange={(e) => handleChange(role.key, g, e.target.value)}
                            style={{
                              ...LEVEL_STYLES[level],
                              padding: "4px 8px",
                              borderRadius: 6,
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: "pointer",
                              outline: "none",
                              appearance: "none",
                              textAlign: "center",
                              opacity: isSaving ? 0.6 : 1,
                            }}
                          >
                            {ACCESS_LEVELS.map((l) => (
                              <option key={l} value={l}>
                                {l === "editable" ? "Editable" : l === "view_only" ? "View Only" : "No Access"}
                              </option>
                            ))}
                          </select>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div style={{ display: "flex", gap: 16, marginTop: 16, flexWrap: "wrap" }}>
        {ACCESS_LEVELS.map((l) => (
          <div key={l} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
            <span style={{ ...LEVEL_STYLES[l], padding: "2px 8px", borderRadius: 4, fontWeight: 700 }}>
              {l === "editable" ? "Editable" : l === "view_only" ? "View Only" : "No Access"}
            </span>
            <span style={{ color: "var(--t2t-text-secondary)" }}>
              {l === "editable" ? "— user can fill/edit fields"
               : l === "view_only" ? "— fields shown but locked"
               : "— tab hidden entirely"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FieldAccessAdmin;
